/**
 * CartPageModern — Feb 2026 (Modern Card Theme · Phase 2)
 *
 * Modern re-skin of the customer cart + checkout surface. Rendered when
 * the user has `ui_theme = 'modern'` (routed via CartPageShell).
 *
 * Behavior parity is 1:1 with the legacy CartPage:
 *   • Same hooks (useCart, useAuth, usePoints, useFlag)
 *   • Same feature-flag gates (loyalty redeem, saved addresses, cash /
 *     cash-app pay-now / cash-app on-delivery)
 *   • Same order-placement + Cash-App prepay + points redeem preview flow
 *
 * Visual language matches CustomerDashboardModern:
 *   – Inter font, warm off-white canvas (var(--am-canvas))
 *   – White rounded cards (.am-card / .am-card-lg)
 *   – Emerald CTA pill (.am-cta) for primary actions
 *   – Matte gold accents for points / rewards
 *   – Red-only for destructive (remove item, cancel)
 *
 * Every interactive element keeps the SAME data-testid as the legacy
 * page so end-to-end tests pass on both themes.
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { DeliverySlotPicker, useDeliverySlots, slotPayload } from '../../components/DeliverySlotPicker';
import { FeeLines } from '../../components/OrderSlotSummary';
import { usePoints } from '../../hooks/usePoints';
import { logEvent } from '../../utils/eventsLogger';
import { Navbar } from '../../components/Navbar';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { CashAppPrepayModal } from '../../components/CashAppPrepayModal';
import { CartInstallNudge } from '../../components/CartInstallNudge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Banknote,
  Smartphone, MapPin, Home, Sparkles, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// ─── helpers (mirrors legacy) ──────────────────────────────────
function addressToString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const parts = [value.line1, value.city, value.state, value.postcode].filter(Boolean);
    return parts.join(', ');
  }
  return String(value);
}
function addressLooksComplete(strOrObj) {
  if (!strOrObj) return false;
  if (typeof strOrObj === 'object') {
    return !!(strOrObj.line1 && strOrObj.city && strOrObj.state && strOrObj.postcode);
  }
  return /\b\d{5}(?:-\d{4})?\b/.test(String(strOrObj));
}
function safePrefillAddress(value) {
  return addressLooksComplete(value) ? addressToString(value) : '';
}
function extractErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// ─── Modern building blocks (locally scoped) ────────────────────
function ModernShell({ children }) {
  return (
    <div
      className="am-modern-scope"
      style={{ minHeight: '100vh', background: 'var(--am-canvas)' }}
      data-testid="cart-page"
    >
      <Navbar />
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 16px 96px' }}>
        {children}
      </div>
    </div>
  );
}

function BackLink({ to = '/customer', label = 'Continue shopping' }) {
  return (
    <Link
      to={to}
      className="am-chip"
      style={{ textDecoration: 'none', fontWeight: 500, cursor: 'pointer' }}
      data-testid="back-to-shop"
    >
      <ChevronLeft size={14} />
      {label}
    </Link>
  );
}

// ─── Empty state ────────────────────────────────────────────────
function EmptyCart() {
  return (
    <ModernShell>
      <div
        className="am-card-lg"
        style={{
          padding: '48px 24px',
          textAlign: 'center',
          maxWidth: 480,
          margin: '48px auto 0',
        }}
      >
        <div
          style={{
            width: 72, height: 72,
            borderRadius: '50%',
            background: 'var(--am-emerald-50)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <ShoppingCart size={32} color="var(--am-emerald-600)" />
        </div>
        <h1
          style={{
            fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em',
            color: 'var(--am-ink-1)', margin: '0 0 8px',
          }}
        >
          Your cart is empty
        </h1>
        <p style={{ color: 'var(--am-ink-3)', fontSize: 14, margin: '0 0 24px' }}>
          Once you add something, it&apos;ll show up here.
        </p>
        <Link to="/customer" style={{ textDecoration: 'none' }}>
          <button
            className="am-cta inline-flex items-center gap-1.5"
            style={{ padding: '12px 24px', fontSize: 14 }}
            data-testid="continue-shopping"
          >
            <ArrowLeft size={16} />
            Continue shopping
          </button>
        </Link>
      </div>
    </ModernShell>
  );
}

// ─── main ──────────────────────────────────────────────────────
export function CartPageModern() {
  const { user } = useAuth();
  const { cartItems, cartTotal, updateQuantity, removeFromCart, placeOrder, isLoading } = useCart();
  const navigate = useNavigate();

  const [deliveryAddress, setDeliveryAddress] = useState(safePrefillAddress(user?.address));
  const [addressWasIncomplete, setAddressWasIncomplete] = useState(
    () => !!user?.address && !addressLooksComplete(user?.address)
  );
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [redeemPts, setRedeemPts] = useState(0);
  const [redeemPreview, setRedeemPreview] = useState(null);
  const [prepayOpen, setPrepayOpen] = useState(false);
  const [prepayLoading, setPrepayLoading] = useState(false);
  const [prepayRender, setPrepayRender] = useState(null);
  const [prepayOrderShort, setPrepayOrderShort] = useState('');

  const loyaltyEnabled = useFlag('customer.loyalty', true);
  const redeemEnabled = useFlag('loyalty.redeem', true);
  const savedAddressesEnabled = useFlag('customer.saved_addresses', true);
  const cashEnabled = useFlag('payment.cash', true);
  const cashAppMaster = useFlag('payment.cash_app', false);
  const cashAppPayNow = useFlag('payment.cashapp_pay_now', false) && cashAppMaster;
  const cashAppOnDelivery = useFlag('payment.cashapp_pay_on_delivery', false) && cashAppMaster;
  const slotsOn = useFlag('customer.scheduled_delivery', false);
  const slotQuote = useDeliverySlots(slotsOn, deliveryAddress);
  const { balance: pointsBalance, refresh: refreshPoints } = usePoints();

  // Auto-pick a valid default payment method when flags change
  useEffect(() => {
    const allowed = [];
    if (cashEnabled) allowed.push('cash');
    if (cashAppPayNow) allowed.push('cashapp_now');
    if (cashAppOnDelivery) allowed.push('cashapp_on_delivery');
    if (allowed.length === 0) return;
    if (!allowed.includes(paymentMethod)) {
      setPaymentMethod(allowed[0]);
    }
  }, [cashEnabled, cashAppPayNow, cashAppOnDelivery, paymentMethod]);

  // Fetch saved addresses when opening checkout
  useEffect(() => {
    if (!showCheckout || user?.role !== 'customer' || !savedAddressesEnabled) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/users/me/addresses`, { withCredentials: true });
        const list = data.addresses || [];
        setSavedAddresses(list);
        const def = list.find(a => a.is_default);
        if (def) {
          setSelectedAddressId(def.id);
          setDeliveryAddress(safePrefillAddress(def.address));
          if (!addressLooksComplete(def.address)) setAddressWasIncomplete(true);
        }
      } catch {
        setSavedAddresses([]);
      }
    })();
  }, [showCheckout, user?.role, savedAddressesEnabled]);

  // Live redemption preview
  useEffect(() => {
    if (!loyaltyEnabled || !redeemEnabled || !redeemPts || redeemPts <= 0) {
      setRedeemPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/users/me/points/redeem-preview`,
          { pts: Number(redeemPts), order_subtotal: Number(cartTotal) },
          { withCredentials: true }
        );
        setRedeemPreview(data);
      } catch {
        setRedeemPreview({ ok: false, error: 'Could not validate redemption' });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [redeemPts, cartTotal, loyaltyEnabled, redeemEnabled]);

  const handleSelectSavedAddress = (addressId) => {
    setSelectedAddressId(addressId);
    if (addressId === 'custom') return;
    const a = savedAddresses.find(x => x.id === addressId);
    if (!a) return;
    setDeliveryAddress(safePrefillAddress(a.address));
    setAddressWasIncomplete(!addressLooksComplete(a.address));
  };

  const handlePlaceOrder = async () => {
    const addr = addressToString(deliveryAddress);
    if (!addr.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }
    if (redeemPts > 0 && redeemPreview && !redeemPreview.ok) {
      toast.error(redeemPreview.error || 'Cannot redeem these points');
      return;
    }
    if (slotsOn && !slotQuote.slot) {
      toast.error(slotQuote.none ? 'No delivery slots available right now' : 'Please choose a delivery time slot');
      return;
    }
    logEvent('checkout.started', {
      item_count: cartItems.length,
      subtotal: cartTotal,
      payment_method: paymentMethod,
      redeem_pts: Number(redeemPts) || 0,
    });
    if (paymentMethod === 'cashapp_now') {
      try {
        const finalTotal = Math.max(0, cartTotal - Number(redeemPreview?.discount_dollars || 0)) + slotQuote.fee;
        const draftShort = ('DRAFT' + Math.random().toString(36).slice(2, 7)).toUpperCase();
        setPrepayLoading(true);
        setPrepayOpen(true);
        const { data } = await axios.post(
          `${API_URL}/api/payment-render/preview`,
          { amount: finalTotal, draft_order_short: draftShort },
          { withCredentials: true },
        );
        setPrepayRender(data);
        setPrepayOrderShort(draftShort);
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Could not load payment info'));
        setPrepayOpen(false);
      } finally {
        setPrepayLoading(false);
      }
      return;
    }
    await submitOrder(paymentMethod);
  };

  const submitOrder = async (methodOverride) => {
    try {
      const order = await placeOrder(
        deliveryAddress,
        methodOverride || paymentMethod,
        notes,
        Number(redeemPts) || 0,
        slotPayload(slotsOn, slotQuote.slot),
      );
      toast.success(
        redeemPts > 0
          ? `Order placed — ${redeemPts} pts redeemed (−${money(redeemPreview?.discount_dollars)})`
          : 'Order placed successfully!'
      );
      refreshPoints();
      setPrepayOpen(false);
      navigate(`/customer/orders/${order.id}`);
    } catch (err) {
      if (err?.message === 'PREVIEW_MODE') return;
      toast.error(extractErrorMessage(err, 'Failed to place order'), { duration: 8000 });
    }
  };

  if (cartItems.length === 0) return <EmptyCart />;

  const finalTotal = (redeemPts > 0 && redeemPreview?.ok
    ? Math.max(0, cartTotal - Number(redeemPreview.discount_dollars))
    : cartTotal) + slotQuote.fee;

  return (
    <ModernShell>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <BackLink />
        <h1
          style={{
            fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em',
            color: 'var(--am-ink-1)', margin: 0,
          }}
        >
          Your cart
        </h1>
        <span className="am-chip am-tnum" style={{ fontSize: 12 }}>
          {cartItems.reduce((sum, i) => sum + i.quantity, 0)} items
        </span>
      </div>

      <div
        className="am-cart-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
          gap: 24,
        }}
      >
        {/* ─── Cart items ─────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cartItems.map(item => (
            <div
              key={item.key}
              className="am-card"
              style={{ padding: 14, display: 'flex', gap: 14, alignItems: 'center' }}
              data-testid={`cart-item-${item.key}`}
            >
              {/* Product image */}
              <div
                style={{
                  width: 72, height: 72,
                  borderRadius: 'var(--am-radius-sm)',
                  background: 'var(--am-inset)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {item.product.image_url ? (
                  <img
                    src={item.product.image_url}
                    alt={item.product.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <ShoppingCart size={24} color="var(--am-ink-3)" />
                )}
              </div>

              {/* Meta */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15, fontWeight: 600,
                    color: 'var(--am-ink-1)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {item.product.name}
                </div>
                <div
                  style={{
                    display: 'flex', gap: 6, alignItems: 'center',
                    marginTop: 4, flexWrap: 'wrap',
                  }}
                >
                  <span className="am-chip" style={{ fontSize: 11, padding: '3px 8px' }}>
                    {item.product.category}
                  </span>
                  {item.size && (
                    <span
                      className="am-chip am-chip-gold"
                      style={{ fontSize: 11, padding: '3px 8px' }}
                    >
                      {item.size}
                    </span>
                  )}
                </div>
                <div
                  className="am-tnum"
                  style={{ fontSize: 12, color: 'var(--am-ink-3)', marginTop: 4 }}
                >
                  {money(item.unitPrice)} each
                </div>
              </div>

              {/* Actions column */}
              <div
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                  gap: 8, minWidth: 128,
                }}
              >
                <button
                  onClick={() => removeFromCart(item.key)}
                  style={{
                    background: 'transparent', border: 'none', padding: 4,
                    cursor: 'pointer', color: '#DC2626',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  title="Remove"
                  data-testid={`remove-item-${item.key}`}
                >
                  <Trash2 size={16} />
                </button>
                <div
                  style={{
                    display: 'flex', alignItems: 'center',
                    borderRadius: 'var(--am-radius-pill)',
                    background: 'var(--am-inset)',
                    padding: 2,
                  }}
                >
                  <button
                    onClick={() => updateQuantity(item.key, item.quantity - 1)}
                    style={{
                      width: 28, height: 28,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--am-emerald-700)', borderRadius: '50%',
                    }}
                    data-testid={`decrease-${item.key}`}
                  >
                    <Minus size={14} />
                  </button>
                  <span
                    className="am-tnum"
                    style={{
                      minWidth: 24, textAlign: 'center', fontSize: 13, fontWeight: 600,
                      color: 'var(--am-ink-1)',
                    }}
                  >
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.key, item.quantity + 1)}
                    style={{
                      width: 28, height: 28,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'var(--am-emerald-600)', border: 'none', cursor: 'pointer',
                      color: 'var(--am-ink-inv)', borderRadius: '50%',
                    }}
                    data-testid={`increase-${item.key}`}
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <span
                  className="am-tnum"
                  style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-ink-1)' }}
                >
                  {money(item.unitPrice * item.quantity)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ─── Summary column (sticky) ─────────────────────── */}
        <div style={{ position: 'sticky', top: 88, alignSelf: 'start' }}>
          <div className="am-card-lg" style={{ padding: 22 }}>
            <div
              className="am-eyebrow"
              style={{ marginBottom: 12, color: 'var(--am-ink-3)' }}
            >
              Order summary
            </div>

            {/* Line items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--am-ink-2)' }}>
                  Subtotal
                </span>
                <span className="am-tnum" style={{ color: 'var(--am-ink-1)', fontWeight: 500 }}>
                  {money(cartTotal)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--am-ink-2)' }}>Delivery</span>
                {slotsOn && !(slotQuote.slot && slotQuote.fee === 0) ? (
                  <span className="am-tnum" style={{ color: 'var(--am-ink-1)', fontWeight: 500 }} data-testid="cart-delivery-fee">
                    {slotQuote.slot ? money(slotQuote.fee) : 'Pick a time'}
                  </span>
                ) : (
                  <span className="am-chip am-chip-emerald" style={{ fontSize: 11 }} data-testid={slotsOn ? 'cart-delivery-fee' : undefined}>
                    Free
                  </span>
                )}
              </div>
              {slotsOn && slotQuote.slot && (
                <FeeLines lines={slotQuote.slot.fee_lines} waived={slotQuote.slot.fee_waived} tier={slotQuote.tier} modern testId="cart-fee-lines" />
              )}

              {/* Points redemption (gold surface) */}
              {loyaltyEnabled && redeemEnabled && pointsBalance > 0 && (
                <div
                  className="am-points-card"
                  style={{ padding: 14, marginTop: 6 }}
                  data-testid="redeem-block"
                >
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: 'var(--am-gold-700)',
                      marginBottom: 8,
                    }}
                  >
                    <Sparkles size={12} />
                    Redeem points · {pointsBalance.toLocaleString()} available
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Input
                      type="number"
                      min={0}
                      max={pointsBalance}
                      value={redeemPts}
                      onChange={(e) => {
                        const v = Math.max(0, Math.min(Number(e.target.value || 0), pointsBalance));
                        setRedeemPts(v);
                      }}
                      placeholder="0"
                      style={{
                        background: 'var(--am-card)',
                        border: '1px solid var(--am-gold-300)',
                        color: 'var(--am-gold-700)',
                        fontVariantNumeric: 'tabular-nums',
                        width: 96,
                        borderRadius: 'var(--am-radius-sm)',
                      }}
                      data-testid="redeem-pts-input"
                    />
                    <button
                      type="button"
                      onClick={() => setRedeemPts(pointsBalance)}
                      style={{
                        background: 'var(--am-gold-500)',
                        color: 'var(--am-ink-inv)',
                        border: 'none',
                        borderRadius: 'var(--am-radius-pill)',
                        padding: '6px 12px',
                        fontSize: 11, fontWeight: 700,
                        cursor: 'pointer',
                      }}
                      data-testid="redeem-max-btn"
                    >
                      MAX
                    </button>
                    {redeemPts > 0 && (
                      <button
                        type="button"
                        onClick={() => setRedeemPts(0)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--am-gold-700)',
                          fontSize: 12, fontWeight: 500,
                          cursor: 'pointer',
                        }}
                        data-testid="redeem-clear-btn"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {redeemPts > 0 && redeemPreview && (
                    <div
                      style={{
                        marginTop: 8, fontSize: 12,
                        color: redeemPreview.ok ? 'var(--am-emerald-700)' : '#DC2626',
                      }}
                      data-testid="redeem-preview"
                    >
                      {redeemPreview.ok
                        ? `−${money(redeemPreview.discount_dollars)} off`
                        : redeemPreview.error}
                    </div>
                  )}
                </div>
              )}

              {redeemPts > 0 && redeemPreview?.ok && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--am-gold-700)' }}>
                    Points discount ({redeemPts} pts)
                  </span>
                  <span
                    className="am-tnum"
                    style={{ color: 'var(--am-gold-700)', fontWeight: 500 }}
                  >
                    −{money(redeemPreview.discount_dollars)}
                  </span>
                </div>
              )}

              {/* Total */}
              <div
                style={{
                  borderTop: '1px solid var(--am-hair)',
                  paddingTop: 12, marginTop: 4,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-ink-2)' }}>
                  Total
                </span>
                <span
                  className="am-tnum"
                  style={{ fontSize: 22, fontWeight: 700, color: 'var(--am-ink-1)', letterSpacing: '-0.01em' }}
                  data-testid="cart-total-final"
                >
                  {money(finalTotal)}
                </span>
              </div>
            </div>

            {/* CTA / Checkout form */}
            {!showCheckout ? (
              <div style={{ marginTop: 20 }}>
                <CartInstallNudge cartItemCount={cartItems.length} />
                <button
                  onClick={() => setShowCheckout(true)}
                  className="am-cta"
                  style={{ width: '100%', height: 48, fontSize: 15, marginTop: 12 }}
                  data-testid="proceed-checkout"
                >
                  Proceed to checkout
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Delivery Address */}
                <div>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: 'var(--am-ink-3)',
                      marginBottom: 6,
                    }}
                  >
                    <MapPin size={12} />
                    Delivery address
                  </div>
                  {savedAddressesEnabled && savedAddresses.length > 0 && (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: 6, marginBottom: 8,
                      }}
                    >
                      {savedAddresses.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleSelectSavedAddress(a.id)}
                          style={{
                            padding: '8px 10px', textAlign: 'left',
                            border: `1px solid ${selectedAddressId === a.id ? 'var(--am-emerald-500)' : 'var(--am-hair)'}`,
                            borderRadius: 'var(--am-radius-sm)',
                            background: selectedAddressId === a.id ? 'var(--am-emerald-50)' : 'var(--am-card)',
                            color: selectedAddressId === a.id ? 'var(--am-emerald-700)' : 'var(--am-ink-1)',
                            cursor: 'pointer',
                            fontSize: 11,
                            transition: 'all .15s ease',
                          }}
                          data-testid={`saved-addr-pick-${a.id}`}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                            <Home size={10} />
                            <span
                              style={{
                                whiteSpace: 'nowrap', overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {a.label}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 10, color: 'var(--am-ink-3)', marginTop: 2,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                          >
                            {addressToString(a.address)}
                          </div>
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleSelectSavedAddress('custom')}
                        style={{
                          padding: '8px 10px', textAlign: 'left',
                          border: `1px dashed ${selectedAddressId === 'custom' ? 'var(--am-gold-500)' : 'var(--am-hair-strong)'}`,
                          borderRadius: 'var(--am-radius-sm)',
                          background: selectedAddressId === 'custom' ? 'var(--am-gold-50)' : 'transparent',
                          color: selectedAddressId === 'custom' ? 'var(--am-gold-700)' : 'var(--am-ink-3)',
                          cursor: 'pointer',
                          fontSize: 11, fontWeight: 700,
                        }}
                        data-testid="use-custom-address"
                      >
                        + NEW
                        <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2 }}>One-time</div>
                      </button>
                    </div>
                  )}
                  {addressWasIncomplete && (
                    <div
                      style={{
                        marginBottom: 8, padding: 10,
                        background: 'var(--am-gold-50)',
                        border: '1px solid var(--am-gold-300)',
                        borderRadius: 'var(--am-radius-sm)',
                        color: 'var(--am-gold-700)',
                        fontSize: 12,
                      }}
                      data-testid="address-incomplete-nudge"
                    >
                      Your saved address is missing city / zip. Please pick a fresh address from the suggestions.
                    </div>
                  )}
                  <AddressAutocomplete
                    value={deliveryAddress}
                    onChange={setDeliveryAddress}
                    placeholder="Start typing your address…"
                    data-testid="delivery-address"
                  />
                </div>

                {slotsOn && <DeliverySlotPicker quote={slotQuote} modern />}

                {/* Payment method */}
                <div>
                  <div
                    style={{
                      fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: 'var(--am-ink-3)',
                      marginBottom: 6,
                    }}
                  >
                    Payment method
                  </div>
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                  >
                    {cashEnabled && (
                      <PaymentOption
                        value="cash"
                        Icon={Banknote}
                        label="Cash on delivery"
                        checked={paymentMethod === 'cash'}
                        onClick={() => setPaymentMethod('cash')}
                        testId="payment-cash"
                      />
                    )}
                    {cashAppPayNow && (
                      <PaymentOption
                        value="cashapp_now"
                        Icon={Smartphone}
                        label="Pay now · Cash App"
                        checked={paymentMethod === 'cashapp_now'}
                        onClick={() => setPaymentMethod('cashapp_now')}
                        testId="payment-cashapp-now"
                      />
                    )}
                    {cashAppOnDelivery && (
                      <PaymentOption
                        value="cashapp_on_delivery"
                        Icon={Smartphone}
                        label="Pay on delivery · Cash App"
                        checked={paymentMethod === 'cashapp_on_delivery'}
                        onClick={() => setPaymentMethod('cashapp_on_delivery')}
                        testId="payment-cashapp-on-delivery"
                      />
                    )}
                  </RadioGroup>
                </div>

                {/* Notes */}
                <div>
                  <Label
                    style={{
                      fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: 'var(--am-ink-3)',
                    }}
                  >
                    Order notes (optional)
                  </Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Special instructions…"
                    rows={2}
                    style={{
                      marginTop: 6,
                      background: 'var(--am-card)',
                      border: '1px solid var(--am-hair)',
                      borderRadius: 'var(--am-radius-sm)',
                      color: 'var(--am-ink-1)',
                      resize: 'none',
                    }}
                    data-testid="order-notes"
                  />
                </div>

                <button
                  onClick={handlePlaceOrder}
                  disabled={isLoading || (slotsOn && slotQuote.none)}
                  className="am-cta"
                  style={{ width: '100%', height: 48, fontSize: 15 }}
                  data-testid="place-order"
                >
                  {isLoading ? 'Placing order…' : 'Place order'}
                </button>

                <button
                  onClick={() => setShowCheckout(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--am-hair)',
                    borderRadius: 'var(--am-radius-pill)',
                    padding: '10px 16px',
                    fontSize: 13, fontWeight: 500,
                    color: 'var(--am-ink-2)',
                    cursor: 'pointer',
                  }}
                  data-testid="back-to-cart"
                >
                  Back to cart
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <CashAppPrepayModal
        open={prepayOpen}
        onClose={() => setPrepayOpen(false)}
        amount={Math.max(0, cartTotal - Number(redeemPreview?.discount_dollars || 0)) + slotQuote.fee}
        orderShortId={prepayOrderShort}
        render={prepayRender}
        loading={prepayLoading}
        onConfirmPaid={() => submitOrder('cashapp_now')}
        onPayOnDelivery={() => {
          setPrepayOpen(false);
          if (cashAppOnDelivery) {
            setPaymentMethod('cashapp_on_delivery');
            toast.info('Switched to Pay on Delivery — placing order…');
            submitOrder('cashapp_on_delivery');
          } else {
            setPaymentMethod('cash');
            toast.info('Switched to Cash on Delivery — placing order…');
            submitOrder('cash');
          }
        }}
      />
    </ModernShell>
  );
}

function PaymentOption({ value, Icon, label, checked, onClick, testId }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 12,
        border: `1px solid ${checked ? 'var(--am-emerald-500)' : 'var(--am-hair)'}`,
        background: checked ? 'var(--am-emerald-50)' : 'var(--am-card)',
        borderRadius: 'var(--am-radius-sm)',
        cursor: 'pointer',
        transition: 'all .15s ease',
      }}
    >
      <RadioGroupItem value={value} id={value} data-testid={testId} />
      <label
        htmlFor={value}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          color: 'var(--am-ink-1)', fontSize: 14, fontWeight: 500,
          cursor: 'pointer', flex: 1,
        }}
      >
        <Icon size={16} color={checked ? 'var(--am-emerald-700)' : 'var(--am-ink-3)'} />
        {label}
      </label>
    </div>
  );
}

export default CartPageModern;
